import { useEffect, useCallback, useRef } from 'react';
import braintree from 'braintree-web';
import { isEmpty } from 'lodash';
import PAYPAL_SETTINGS_QUERY from './usePaypal/paypalSettings.query.graphql';
import { usePaypalState } from '../components/PaypalStateProvider';
import { useBookingPath, useCart, useSSRQuery } from '../graphql';
import {
  clearPaypalFromSession,
  PAYPAL_NONCE,
  PAYPAL_PAYER_ID,
  PAYPAL_DEVICE_DATA,
  PAYPAL_BILLING_ADDRESS,
  PAYPAL_AUTHORISED_AMOUNT,
  PAYPAL_BUTTON_TYPE,
} from '@g4/next-furniture/utils/paypalUtils';

import {
  PAYPAL,
  PAYPAL_PAYLATER,
} from '../pageComponents/paymentPage/constants';

const usePaypal = (paypalComponent, balanceDue) => {
  const paypalAmountRef = useRef('');
  const { cart } = useCart();

  const {
    setIsPaypalAvailable,
    setBrainTreeClient,
    setPaypalCheckoutClient,
    paypalCheckoutClient,
    brainTreeClient,
    setIsPaypalRendered,
    setIsPaypalButtonRendered,
    isPaypalRendered,
    setIsPaypalAuthorized,
    setPaypalButtonType,
    isPaypalAuthorized,
    setTravelInsuranceEnabledForPaypal,
    setPaypalBannersEnabled,
  } = usePaypalState();

  const { isLoyaltyPointsApplied, setIsLoyaltyPointsApplied } =
    useBookingPath();

  useEffect(() => {
    const loyaltyPointsPayment = cart?.order?.payments?.find(
      item => item.paymentMethod === 'LOYALTY_POINTS'
    );
    if (loyaltyPointsPayment) {
      setIsLoyaltyPointsApplied(true);
    }
  }, [cart, setIsLoyaltyPointsApplied]);

  const createPaypalCheckoutClient = useCallback(
    brainTreeInstance => {
      return braintree.paypalCheckout
        .create({
          client: brainTreeInstance,
        })
        .then(paypalCheckoutInstance => {
          setPaypalCheckoutClient(paypalCheckoutInstance);
        });
    },
    [setPaypalCheckoutClient]
  );

  const initiateBrainTreeClient = useCallback(
    clientToken => {
      return braintree.client
        .create({
          authorization: clientToken,
        })
        .catch(error => {
          if (error) {
            setIsPaypalAvailable(false);
            return;
          }
        })
        .then(brainTreeInstance => {
          setBrainTreeClient(brainTreeInstance);
          createPaypalCheckoutClient(brainTreeInstance);
        });
    },
    [setBrainTreeClient, createPaypalCheckoutClient, setIsPaypalAvailable]
  );

  const collectDeviceData = useCallback(brainTreeInstance => {
    braintree.dataCollector
      .create({
        client: brainTreeInstance,
      })
      .then(dataCollectorInstance => {
        window.sessionStorage.setItem(
          PAYPAL_DEVICE_DATA,
          dataCollectorInstance.deviceData
        );
      });
  }, []);

  const handlePaymentSuccess = useCallback(
    data => {
      return paypalCheckoutClient
        .tokenizePayment(data)
        .then(function (payload) {
          if (payload.nonce) {
            window.sessionStorage.setItem(PAYPAL_NONCE, payload.nonce);
            window.sessionStorage.setItem(
              PAYPAL_PAYER_ID,
              payload.details.payerId
            );
            const paypalBillingAddress = {
              ...payload.details.billingAddress,
              firstName: payload.details.firstName,
              lastName: payload.details.lastName,
              email: payload.details.email,
              phoneNumber: payload.details.phone.replaceAll('-', ''),
            };

            window.sessionStorage.setItem(
              PAYPAL_BILLING_ADDRESS,
              JSON.stringify(paypalBillingAddress)
            );
            setIsPaypalAuthorized(true);
          }
        });
    },
    [paypalCheckoutClient, setIsPaypalAuthorized]
  );

  const createOrder = useCallback(() => {
    return paypalCheckoutClient.createPayment({
      flow: 'checkout',
      amount: paypalAmountRef.current ? paypalAmountRef.current.toFixed(2) : 0,
      currency: 'USD',
      requestBillingAgreement: true,
    });
  }, [paypalCheckoutClient, paypalAmountRef]);

  /*global paypal*/
  //Pay Now Button
  const createPayNowCheckoutButton = useCallback(() => {
    return paypal
      .Buttons({
        style: {
          color: 'gold',
          height: 38,
        },
        fundingSource: paypal.FUNDING.PAYPAL,
        createOrder: createOrder,
        onApprove: handlePaymentSuccess,
        onCancel: handlePaymentFailure,
        onError: handlePaymentCanceled,
      })
      .render('#paynow-button');
  }, [createOrder, handlePaymentSuccess]);

  // Pay later button
  const createPayLaterCheckoutButton = useCallback(() => {
    try {
      return paypal
        .Buttons({
          style: {
            color: 'gold',
            height: 38,
          },
          fundingSource: paypal.FUNDING.PAYLATER,
          createOrder: createOrder,
          onApprove: handlePaymentSuccess,
          onCancel: handlePaymentFailure,
          onError: handlePaymentCanceled,
        })
        .render('#paylater-button');
    } catch (error) {
      console.log(error);
    }
  }, [createOrder, handlePaymentSuccess]);

  const createPaypalButtons = useCallback(
    (paypalCheckoutInstance, buttonType) => {
      return paypalCheckoutInstance
        .loadPayPalSDK({
          components: 'messages,buttons',
          currency: 'USD',
          dataAttributes: {
            amount: paypalAmountRef.current
              ? paypalAmountRef.current.toFixed(2)
              : 0,
          },
        })
        .then(() => {
          setIsPaypalButtonRendered(true);
          if (buttonType === PAYPAL) {
            createPayNowCheckoutButton();
          } else if (buttonType === PAYPAL_PAYLATER) {
            createPayLaterCheckoutButton();
          }
        });
    },
    [
      createPayLaterCheckoutButton,
      paypalAmountRef,
      createPayNowCheckoutButton,
      setIsPaypalButtonRendered,
    ]
  );

  //Paypal pay later message
  const createPaypalMessage = useCallback(
    paypalCheckoutInstance => {
      paypalCheckoutInstance
        .loadPayPalSDK({
          components: 'messages',
          currency: 'USD',
          'enable-funding': 'paylater',
          dataAttributes: {
            amount: paypalAmountRef.current
              ? paypalAmountRef.current.toFixed(2)
              : 0,
          },
        })
        .then(() => {
          setIsPaypalRendered(true);
        });
    },
    [paypalAmountRef, setIsPaypalRendered]
  );

  const handlePaymentFailure = error => {
    console.log('PayPal payment failure', error);
  };

  const handlePaymentCanceled = error => {
    console.log('PayPal payment canceled', error);
  };

  const { data: paypalSettings } = useSSRQuery(PAYPAL_SETTINGS_QUERY);
  const clientToken = paypalSettings?.settings?.paypal?.clientToken ?? null;
  const isPaypalEnabled = paypalSettings?.settings?.paypal?.enabled ?? false;
  const travelInsuranceEnabledForPaypal =
    paypalSettings?.settings?.paypal?.travelInsuranceEnabled ?? false;
  const paypalReauthorizationPercentage =
    paypalSettings?.settings?.paypal?.reauthorizationPercentage ?? 0;
  const isPaypalBannersEnabled =
    paypalSettings?.application?.configurations?.paypalBannersEnabled ?? false;

  // set is travel insurance enabled and banner enabled for paypal

  useEffect(() => {
    setTravelInsuranceEnabledForPaypal(travelInsuranceEnabledForPaypal);
    setPaypalBannersEnabled(isPaypalBannersEnabled);
  }, [
    travelInsuranceEnabledForPaypal,
    setTravelInsuranceEnabledForPaypal,
    setPaypalBannersEnabled,
    isPaypalBannersEnabled,
  ]);

  // initiate paypal pay later message component
  useEffect(() => {
    if (!isEmpty(paypalCheckoutClient) && isPaypalRendered === false) {
      createPaypalMessage(paypalCheckoutClient);
    }
  }, [paypalCheckoutClient, isPaypalRendered, createPaypalMessage]);

  // initiate brain tree client and paypal checkout instance
  useEffect(() => {
    if (isPaypalEnabled === true) {
      setIsPaypalAvailable(isPaypalEnabled);
      initiateBrainTreeClient(clientToken);
      paypalAmountRef.current = balanceDue;
    }
  }, [
    isPaypalEnabled,
    setIsPaypalAvailable,
    clientToken,
    initiateBrainTreeClient,
    balanceDue,
  ]);
  // clear paypal token
  useEffect(() => {
    const paypalAuthorizedAmount = window.sessionStorage.getItem(
      PAYPAL_AUTHORISED_AMOUNT
    )
      ? parseFloat(window.sessionStorage.getItem(PAYPAL_AUTHORISED_AMOUNT))
      : 0;
    const amountChangeInPercentage = Math.abs(
      ((balanceDue - paypalAuthorizedAmount) / paypalAuthorizedAmount) * 100
    );
    // clear paypal token if total amount change is +/- 15% or
    // clear paypal token if loyalty points applied and cart amount changed
    if (
      (paypalComponent === 'buttons' &&
        amountChangeInPercentage >= paypalReauthorizationPercentage) ||
      (amountChangeInPercentage > 0 && isLoyaltyPointsApplied)
    ) {
      clearPaypalFromSession();
      setIsPaypalButtonRendered(false);
      setIsPaypalRendered(false);
      setIsPaypalAuthorized(false);
      setIsLoyaltyPointsApplied(false);
      setPaypalButtonType('');
    }
    if (window.sessionStorage.getItem(PAYPAL_NONCE)) {
      setIsPaypalAuthorized(true);
    } else {
      setIsPaypalAuthorized(false);
    }

    const paypalBtnType = window.sessionStorage.getItem(PAYPAL_BUTTON_TYPE);
    if (!isEmpty(paypalBtnType) && isPaypalAuthorized) {
      setPaypalButtonType(paypalBtnType);
    }
  }, [
    setIsPaypalAuthorized,
    balanceDue,
    paypalComponent,
    paypalReauthorizationPercentage,
    isLoyaltyPointsApplied,
    setIsLoyaltyPointsApplied,
    setIsPaypalRendered,
    setIsPaypalButtonRendered,
    setPaypalButtonType,
    isPaypalAuthorized,
  ]);

  const displayPaypalButtons = (total, buttonType) => {
    setIsPaypalButtonRendered(false);
    paypalAmountRef.current = total;
    window.sessionStorage.setItem(PAYPAL_AUTHORISED_AMOUNT, total);
    if (total !== 0) {
      collectDeviceData(brainTreeClient);
      createPaypalButtons(paypalCheckoutClient, buttonType);
    }
  };

  return {
    displayPaypalButtons,
  };
};

export default usePaypal;
